// 디렉토리 경로: client/src/components/management/
// 파일명: ScriptEditor.tsx
// 파일 코드의 역할/설명: 스크립트 작성 및 편집 에디터 (연재 datalist, 회차 자동제안, 완성 체크박스 지원)

// ============================================================================
// 1. Imports
// ============================================================================
import { useState, useMemo, useCallback } from 'react'
import { useScripts } from '@/hooks'
import { useAppStore } from '@/stores'
import type { Script } from '@/types'
import styles from './ScriptEditor.module.css'
import { CharacterCounter } from './CharacterCounter'
import { CategoryFilter } from './CategoryFilter'
import { ExpandModeModal } from './ExpandModeModal'
import { ReferenceLoader } from './ReferenceLoader'
import type { ReferenceItem } from '@/types'

// ============================================================================
// 2. Types
// ============================================================================
interface ScriptEditorProps {
  script: Script
  onClose: () => void
  onDelete?: (id: string) => void
}

// ============================================================================
// 3. Constants & Helpers
// ============================================================================
// 변수 추출 정규식: {{변수명}} 형태
const VARIABLE_REGEX = /\{\{(\w+)\}\}/g

/**
 * 이름에서 회차 추출: 'N강/N화/N회/N편' 패턴 우선(예: '1-1강' -> '1'),
 * 없으면 선행 1~3자리 숫자만(예: '01 도입' -> '1').
 * 4자리 이상 선행 숫자는 날짜코드(예: '2608_')로 간주해 제안하지 않음.
 */
function parseLeadingEpisode(text: string): string {
  const marker = text.match(/(\d{1,3})\s*(?:강|화|회|편)/)
  if (marker) {
    const num = parseInt(marker[1], 10)
    if (!isNaN(num) && num > 0) return String(num)
  }
  const match = text.match(/^\s*(\d{1,3})(?!\d)/)
  if (match) {
    const num = parseInt(match[1], 10)
    if (!isNaN(num) && num > 0) return String(num)
  }
  return ''
}

// ============================================================================
// 4. ScriptEditor Component
// ============================================================================
export function ScriptEditor({ script, onClose, onDelete }: ScriptEditorProps) {
  // ========================================================================
  // 4-1. Hooks & Stores
  // ========================================================================
  const { saveScript, editScript, categories, scripts } = useScripts()
  const showToast = useAppStore((state) => state.showToast)

  // ========================================================================
  // 4-2. 편집 상태
  // ========================================================================
  const [name, setName] = useState(script.name)
  const [content, setContent] = useState(script.content)
  const [variableValues, setVariableValues] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)

  // ========================================================================
  // 4-2-B. [P5-01] 새로운 상태 변수 (Phase 5)
  // ========================================================================
  const [category, setCategory] = useState(script.category ?? '')
  const [llmModel, setLlmModel] = useState(script.llmModel ?? '')
  const [customLlmModel, setCustomLlmModel] = useState('')

  // ========================================================================
  // 4-2-C. [SC-01] 연재 / 회차 / 완성 상태 변수 (Script Collection)
  // ========================================================================
  const [seriesId, setSeriesId] = useState(script.seriesId ?? '')
  const [episode, setEpisode] = useState<string>(
    script.episode !== undefined
      ? String(script.episode)
      : parseLeadingEpisode(script.name)
  )
  const [isCompleted, setIsCompleted] = useState<boolean>(Boolean(script.completedAt))

  // 기존 seriesId 목록 (datalist 자동완성용)
  const seriesSuggestions = useMemo(() => {
    const set = new Set<string>()
    scripts.forEach((s) => {
      if (s.seriesId?.trim()) {
        set.add(s.seriesId.trim())
      }
    })
    return Array.from(set).sort()
  }, [scripts])

  // ========================================================================
  // 4-2-D. [P9-01] 확대 모드 상태 (Phase 9)
  // ========================================================================
  const [isExpandMode, setIsExpandMode] = useState(false)

  // ========================================================================
  // 4-2-E. [P7-01] 레퍼런스 로더 상태 (Phase 7)
  // ========================================================================
  const [isReferenceLoaderOpen, setIsReferenceLoaderOpen] = useState(false)

  // ========================================================================
  // 4-3. 변수 목록 추출 (콘텐츠에서 {{...}} 패턴 감지)
  // ========================================================================
  const variables = useMemo(() => {
    const matches = content.matchAll(VARIABLE_REGEX)
    const vars = new Set<string>()
    for (const match of matches) {
      vars.add(match[1])
    }
    return Array.from(vars)
  }, [content])

  // ========================================================================
  // 4-4. 미리보기 생성 (변수 값으로 치환)
  // ========================================================================
  const preview = useMemo(() => {
    let result = content
    for (const [key, value] of Object.entries(variableValues)) {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || `{{${key}}}`)
    }
    return result
  }, [content, variableValues])

  // ========================================================================
  // 4-5. 변수 값 변경 핸들러
  // ========================================================================
  const handleVariableChange = useCallback((variable: string, value: string) => {
    setVariableValues((prev) => ({ ...prev, [variable]: value }))
  }, [])

  // 이름 변경 시 회차 번호 자동 제안
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value
      setName(newName)
      if (!episode.trim()) {
        const suggested = parseLeadingEpisode(newName)
        if (suggested) {
          setEpisode(suggested)
        }
      }
    },
    [episode]
  )

  // ========================================================================
  // 4-6. 저장 핸들러 (Firestore 연동)
  // ========================================================================
  const handleSave = useCallback(async () => {
    // 유효성 검사
    if (!name.trim()) {
      alert('스크립트 이름을 입력하세요.')
      return
    }

    setIsSaving(true)

    try {
      // [P5-03] 실제 LLM 모델 값 결정
      const actualLlmModel = llmModel === 'custom' ? customLlmModel : llmModel

      // [SC-01, U-F2, U-F3] 회차 필드 처리:
      // 1. 미수정 시 원값(소수 회차 1.5 등) 그대로 보존
      // 2. 신규 입력 시 정수만 허용
      // 3. 비운 입력은 undefined(명시적 삭제)
      let resolvedEpisode: number | undefined = undefined
      const trimmedEpisode = episode.trim()
      if (trimmedEpisode === '') {
        resolvedEpisode = undefined
      } else if (script.episode !== undefined && trimmedEpisode === String(script.episode)) {
        resolvedEpisode = script.episode
      } else if (/^\d+$/.test(trimmedEpisode)) {
        const num = parseInt(trimmedEpisode, 10)
        if (!isNaN(num) && num > 0) {
          resolvedEpisode = num
        }
      }

      // [U-F2] 연재명 필드 처리: 비운 입력은 undefined(명시적 삭제)
      const resolvedSeriesId = seriesId.trim() || undefined

      // [SC-01] 완성 상태 처리
      const resolvedCompletedAt = isCompleted
        ? script.completedAt
          ? new Date(script.completedAt)
          : new Date()
        : undefined

      const scriptData = {
        name: name.trim(),
        content,
        variables,
        // [P5-03] 새 필드 추가
        category: category.trim() || undefined,
        llmModel: actualLlmModel || undefined,
        llmModelType: script.llmModelType || undefined,
        // [SC-01, U-F2, U-F3] 스크립트 모음/연재 필드
        seriesId: resolvedSeriesId,
        episode: resolvedEpisode,
        completedAt: resolvedCompletedAt,
      }

      if (script.id) {
        // 수정 (기존 스크립트)
        const success = await editScript(script.id, scriptData)
        if (!success) {
          showToast('저장 실패', 'error')
          return
        }
        console.log('[ScriptEditor] 스크립트 수정 완료:', script.id)
      } else {
        // 생성 (새 스크립트)
        const created = await saveScript(scriptData)
        if (!created) {
          showToast('저장 실패', 'error')
          return
        }
        console.log('[ScriptEditor] 스크립트 생성 완료')
      }

      onClose()
    } catch (err) {
      console.error('[ScriptEditor] 저장 실패:', err)
      showToast('저장 실패', 'error')
    } finally {
      setIsSaving(false)
    }
  }, [
    name,
    content,
    variables,
    category,
    llmModel,
    customLlmModel,
    seriesId,
    episode,
    isCompleted,
    script.id,
    script.episode,
    script.completedAt,
    script.llmModelType,
    saveScript,
    editScript,
    showToast,
    onClose,
  ])


  // ========================================================================
  // 4-7. 삭제 핸들러
  // ========================================================================
  const handleDelete = useCallback(() => {
    if (script.id && onDelete) {
      onDelete(script.id)
    }
  }, [script.id, onDelete])

  // ========================================================================
  // 4-8. 미리보기 복사 핸들러
  // ========================================================================
  const handleCopyPreview = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(preview)
      alert('클립보드에 복사되었습니다.')
      console.log('[ScriptEditor] 클립보드 복사 완료')
    } catch (err) {
      console.error('[ScriptEditor] 클립보드 복사 실패:', err)
      alert('복사에 실패했습니다.')
    }
  }, [preview])

  // ========================================================================
  // 4-9. [P9-01] 확대 모드 저장 핸들러 (Phase 9)
  // ========================================================================
  const handleExpandSave = useCallback(
    async (newContent: string) => {
      // 확대 모드에서는 content만 업데이트
      // 실제 저장은 기존 handleSave 버튼 클릭 시 수행
      setContent(newContent)
      console.log('[ScriptEditor] 확대 모드 content 업데이트 완료')
    },
    []
  )

  // ========================================================================
  // 4-10. [P7-01] 레퍼런스 선택 핸들러 (Phase 7)
  // ========================================================================
  const handleReferenceSelect = useCallback((item: ReferenceItem) => {
    // 레퍼런스 내용을 content에 추가 (기존 내용 뒤에)
    setContent((prev) => {
      if (prev) {
        return `${prev}\n\n${item.content}`
      }
      return item.content
    })
    console.log('[ScriptEditor] 레퍼런스 불러오기 완료:', item.title)
  }, [])

  // ========================================================================
  // 4-10. Render
  // ========================================================================
  return (
    <div className={styles.scriptEditor}>
      {/* ================================================================
          헤더 영역
          ================================================================ */}
      <div className={styles.editorHeader}>
        <input
          type="text"
          className={styles.nameInput}
          value={name}
          onChange={handleNameChange}
          placeholder="스크립트 이름"
          disabled={isSaving}
        />
        <div className={styles.editorActions}>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={styles.saveBtn}
          >
            {isSaving ? '저장 중...' : '저장'}
          </button>
          {script.id && onDelete && (
            <button
              onClick={handleDelete}
              disabled={isSaving}
              className={styles.deleteBtn}
            >
              삭제
            </button>
          )}
          <button onClick={onClose} disabled={isSaving}>
            닫기
          </button>
        </div>
      </div>

      {/* ================================================================
          현재 메타데이터 배지 (연재, 회차, 완성, 카테고리, SNS)
          ================================================================ */}
      {(category || llmModel || seriesId || episode || isCompleted) && (
        <div className={styles.editorBadges}>
          {seriesId && (
            <span className={styles.editorBadge}>
              📖 {seriesId}
            </span>
          )}
          {episode && (
            <span className={styles.editorBadge}>
              🔢 {episode}화
            </span>
          )}
          {isCompleted && (
            <span
              className={styles.editorBadge}
              style={{ background: '#e6fcf5', color: '#0ca678', borderColor: '#63e6be' }}
            >
              ✅ 완성
            </span>
          )}
          {category && (
            <span className={styles.editorBadge}>
              📁 {category}
            </span>
          )}
          {llmModel && llmModel !== 'custom' && (
            <span className={styles.editorBadge}>
              📱 {llmModel}
            </span>
          )}
          {llmModel === 'custom' && customLlmModel && (
            <span className={styles.editorBadge}>
              📱 {customLlmModel}
            </span>
          )}
        </div>
      )}

      {/* ================================================================
          에디터 본문 영역
          ================================================================ */}
      <div className={styles.editorBody}>
        {/* ================================================================
            [P5-04] 메타데이터 영역
            ================================================================ */}
        <div className={styles.metadataSection}>
          {/* 연재 (시리즈) - datalist 자동완성 */}
          <div className={styles.formGroup}>
            <label htmlFor="series-input">연재 (시리즈)</label>
            <input
              id="series-input"
              type="text"
              list="series-suggestions-list"
              value={seriesId}
              onChange={(e) => setSeriesId(e.target.value)}
              placeholder="연재명 (직접 입력 또는 선택)"
              disabled={isSaving}
              className={styles.textInput}
            />
            <datalist id="series-suggestions-list">
              {seriesSuggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>

          {/* 회차 (정수) */}
          <div className={styles.formGroup}>
            <label htmlFor="episode-input">회차</label>
            <input
              id="episode-input"
              type="number"
              min="1"
              step="any"
              value={episode}
              onChange={(e) => {
                const val = e.target.value
                if (val === '' || /^\d+$/.test(val)) {
                  setEpisode(val)
                }
              }}
              placeholder="회차 번호 (예: 1)"
              disabled={isSaving}
              className={styles.textInput}
            />
          </div>

          {/* 완성 체크박스 */}
          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={isCompleted}
                onChange={(e) => setIsCompleted(e.target.checked)}
                disabled={isSaving}
              />
              <span>✅ 완성 스크립트</span>
            </label>
          </div>

          {/* 카테고리 */}
          <div className={styles.formGroup}>
            <label>카테고리</label>
            <CategoryFilter
              value={category}
              onChange={setCategory}
              categories={categories}
              disabled={isSaving}
            />
          </div>

          {/* 사용 SNS (레거시 호환: llmModel 필드 재활용) */}
          <div className={styles.formGroup}>
            <label>사용 SNS</label>
            <select
              value={llmModel}
              onChange={(e) => setLlmModel(e.target.value)}
              disabled={isSaving}
              className={styles.selectInput}
            >
              <option value="">선택하세요</option>
              <option value="유튜브 스크립트">유튜브 스크립트</option>
              <option value="네이버 블로그 스크립트">네이버 블로그 스크립트</option>
              <option value="전자책 스크립트">전자책 스크립트</option>
              <option value="인스타그램 스크립트">인스타그램 스크립트</option>
              <option value="페이스북 스크립트">페이스북 스크립트</option>
              <option value="트위터(X) 스크립트">트위터(X) 스크립트</option>
              <option value="틱톡 스크립트">틱톡 스크립트</option>
              <option value="링크드인 스크립트">링크드인 스크립트</option>
              <option value="카카오톡 채널 스크립트">카카오톡 채널 스크립트</option>
              <option value="custom">직접 입력</option>
            </select>
            {llmModel === 'custom' && (
              <input
                type="text"
                value={customLlmModel}
                onChange={(e) => setCustomLlmModel(e.target.value)}
                placeholder="SNS 플랫폼 직접 입력"
                disabled={isSaving}
                className={styles.customModelInput}
              />
            )}
          </div>

        </div>

        {/* ================================================================
            스크립트 입력
            ================================================================ */}
        <div className={styles.inputSection}>
          <div className={styles.inputHeader}>
            <label>스크립트 내용</label>
            <div className={styles.inputActions}>
              <button
                type="button"
                onClick={() => setIsExpandMode(true)}
                disabled={isSaving}
                className={styles.expandContentBtn}
                title="전체 화면 확대 모드"
              >
                🔍 확대
              </button>
              <button
                type="button"
                onClick={() => setIsReferenceLoaderOpen(true)}
                disabled={isSaving}
                className={styles.loadReferenceBtn}
                title="저장된 글 또는 트래킹에서 레퍼런스 불러오기"
              >
                📥 불러오기
              </button>
            </div>
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="스크립트 내용을 입력하세요."
            rows={10}
            disabled={isSaving}
          />
          {/* [P5-04] 글자 수 카운터 추가 */}
          <CharacterCounter current={content.length} />
        </div>

        {/* 변수 입력 (변수가 있을 때만 표시) */}
        {variables.length > 0 && (
          <div className={styles.variableSection}>
            <label>변수 값 입력</label>
            <div className={styles.variableInputs}>
              {variables.map((variable) => (
                <div key={variable} className={styles.variableInput}>
                  <span>{`{{${variable}}}`}</span>
                  <input
                    type="text"
                    value={variableValues[variable] || ''}
                    onChange={(e) => handleVariableChange(variable, e.target.value)}
                    placeholder={`${variable} 값 입력`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 미리보기 */}
        <div className={styles.previewSection}>
          <div className={styles.previewHeader}>
            <label>미리보기</label>
            <button onClick={handleCopyPreview}>복사</button>
          </div>
          <div className={styles.preview}>
            {preview}
          </div>
        </div>
      </div>

      {/* ================================================================
          [P9-02] 확대 모드 모달 (Phase 9)
          ================================================================ */}
      <ExpandModeModal
        isOpen={isExpandMode}
        script={{
          ...script,
          name,
          content,
          category,
          llmModel: llmModel === 'custom' ? customLlmModel : llmModel,
        }}
        onClose={() => setIsExpandMode(false)}
        onSave={handleExpandSave}
      />

      {/* ================================================================
          [P7-01] 레퍼런스 로더 모달 (Phase 7)
          ================================================================ */}
      <ReferenceLoader
        isOpen={isReferenceLoaderOpen}
        onClose={() => setIsReferenceLoaderOpen(false)}
        onSelect={handleReferenceSelect}
      />
    </div>
  )
}

export default ScriptEditor
